// src/utils/imageLoader.ts
// Task 4: Image loader with fallback for product photos

import { useState, useEffect } from 'react';

/**
 * Custom hook для загрузки изображений с fallback
 */
export const useImageWithFallback = (url: string, fallback?: string) => {
  const[src, setSrc] = useState<string>(url);
  const[isLoading, setIsLoading] = useState<boolean>(true);
  const[hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    // Сбрасываем состояние при изменении URL
    setSrc(url);
    setIsLoading(true);
    setHasError(false);

    const img = new Image();

    const handleLoad = () => {
      setIsLoading(false);
      setHasError(false);
    };

    const handleError = () => {
      console.warn(`⚠️ Ошибка загрузки изображения: ${url}`);
      if (fallback) {
        setSrc(fallback);
      } else {
        // Если fallback не указан, оставляем пустым
        setSrc('');
      }
      setIsLoading(false);
      setHasError(true);
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);
    img.src = url;

    // Cleanup
    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
    };
  },[url, fallback]);

  return { src, isLoading, hasError };
};

/**
 * Генерирует градиент-fallback по ID продукта
 */
export const getProductGradient = (productId: string): string => {
  // Простой хэш для консистентного выбора цвета
  const hash = productId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const gradients =[
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  ];
  return gradients[hash % gradients.length];
};

/**
 * Предзагрузка изображения с retry логикой
 */
export const preloadImage = async (
  url: string,
  retries: number = 2
): Promise<boolean> => {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
      });
      return true;
    } catch (error) {
      if (attempt === retries) {
        console.error(`❌ Не удалось загрузить изображение после ${retries + 1} попыток: ${url}`);
        return false;
      }
      // Ждем перед следующей попыткой
      await new Promise((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
    }
  }
  return false;
};
